import React, { useCallback, useEffect, useId, useRef } from 'react';
import { CircleAction } from '@domain/models';
import { useArcAnimation } from '../ArcRing/useArcAnimation';
import styles from './LungAnimation.module.css';

// ── Leaf clusters: primary + satellite per level-4 tip, rotation = branch tangent angle
// SVG transform="rotate(A) scale(s)" on an ellipse inside translate(cx,cy) group:
//   scale collapses/expands from group origin (= leaf center); rotate tilts in place.
interface LeafDef { cx: number; cy: number; rot: number; rx?: number; ry?: number; }
// Interleaved right/left so each mirrored pair shares the same stagger slot.
// No satellite leaves — tip density already causes overlap in the upper cluster.
// Instead, extra leaves sit at branch fork intersections for natural coverage.
const LEAVES: LeafDef[] = [
  // ── 9 primary tip pairs (exact branch endpoints, interleaved R/L) ──
  { cx: 177, cy: 66,  rot: -14 }, { cx: 103, cy: 66,  rot:  14 },  // pair 0
  { cx: 203, cy: 70,  rot:  24 }, { cx:  77, cy: 70,  rot: -24 },  // pair 1
  { cx: 219, cy: 73,  rot:   8 }, { cx:  61, cy: 73,  rot:  -8 },  // pair 2
  { cx: 227, cy: 110, rot:  72 }, { cx:  53, cy: 110, rot: -72 },  // pair 3
  { cx: 235, cy: 102, rot:   9 }, { cx:  45, cy: 102, rot:  -9 },  // pair 4
  { cx: 249, cy: 154, rot:  17 }, { cx:  31, cy: 154, rot: -17 },  // pair 5
  { cx: 240, cy: 67,  rot:  -5 }, { cx:  40, cy: 67,  rot:   5 },  // pair 6
  { cx: 238, cy: 151, rot: -34 }, { cx:  42, cy: 151, rot:  34 },  // pair 7
  { cx: 246, cy: 200, rot: -13 }, { cx:  34, cy: 200, rot:  13 },  // pair 8

  // ── Intersection leaves at branch forks (smaller, interleaved R/L) ──
  // level-3 forks: 184,147 (R) / 96,148 (L)
  { cx: 180, cy: 151, rot:  15, rx: 1.9, ry: 4.2 },
  { cx: 100, cy: 151, rot: -15, rx: 1.9, ry: 4.2 },
  // level-3 forks: 205,176 (R) / 75,178 (L)
  { cx: 210, cy: 180, rot:  -5, rx: 1.9, ry: 4.2 },
  { cx:  70, cy: 180, rot:   5, rx: 1.9, ry: 4.2 },
  // level-2 forks: 170,191 (R) / 110,194 (L)
  { cx: 165, cy: 195, rot:  50, rx: 1.6, ry: 3.6 },
  { cx: 115, cy: 195, rot: -50, rx: 1.6, ry: 3.6 },

  // ── 48 satellites — 2 per primary/fork, placed on BOTH sides (perpendicular
  //    to the branch axis) so they fan out like a real leaf cluster, not a kebab.
  //    Offset D=7 along perp direction (cos rot, sin rot). rx/ry smaller.
  // pair 0  (177,66,−14°) → perp=(0.97,−0.24)
  { cx: 170, cy:  68, rot: -34, rx: 1.4, ry: 3.0 }, { cx: 184, cy:  64, rot:   6, rx: 1.4, ry: 3.0 },
  // pair 0L (103,66, 14°) → perp=(0.97, 0.24)
  { cx:  96, cy:  64, rot:  -6, rx: 1.4, ry: 3.0 }, { cx: 110, cy:  68, rot:  34, rx: 1.4, ry: 3.0 },
  // pair 1  (203,70, 24°) → perp=(0.91, 0.41)
  { cx: 197, cy:  67, rot:   4, rx: 1.4, ry: 3.0 }, { cx: 209, cy:  73, rot:  44, rx: 1.4, ry: 3.0 },
  // pair 1L ( 77,70,−24°) → perp=(0.91,−0.41)
  { cx:  71, cy:  73, rot: -44, rx: 1.4, ry: 3.0 }, { cx:  83, cy:  67, rot:  -4, rx: 1.4, ry: 3.0 },
  // pair 2  (219,73,  8°) → perp=(0.99, 0.14)
  { cx: 212, cy:  72, rot: -12, rx: 1.4, ry: 3.0 }, { cx: 226, cy:  74, rot:  28, rx: 1.4, ry: 3.0 },
  // pair 2L ( 61,73, −8°) → perp=(0.99,−0.14)
  { cx:  54, cy:  74, rot: -28, rx: 1.4, ry: 3.0 }, { cx:  68, cy:  72, rot:  12, rx: 1.4, ry: 3.0 },
  // pair 3  (227,110, 72°) → perp=(0.31, 0.95)
  { cx: 225, cy: 103, rot:  52, rx: 1.4, ry: 3.0 }, { cx: 229, cy: 117, rot:  92, rx: 1.4, ry: 3.0 },
  // pair 3L ( 53,110,−72°) → perp=(0.31,−0.95)
  { cx:  55, cy: 103, rot: -52, rx: 1.4, ry: 3.0 }, { cx:  51, cy: 117, rot: -92, rx: 1.4, ry: 3.0 },
  // pair 4  (235,102,  9°) → perp=(0.99, 0.16)
  { cx: 228, cy: 101, rot: -11, rx: 1.4, ry: 3.0 }, { cx: 242, cy: 103, rot:  29, rx: 1.4, ry: 3.0 },
  // pair 4L ( 45,102, −9°) → perp=(0.99,−0.16)
  { cx:  38, cy: 103, rot: -29, rx: 1.4, ry: 3.0 }, { cx:  52, cy: 101, rot:  11, rx: 1.4, ry: 3.0 },
  // pair 5  (249,154, 17°) → perp=(0.96, 0.29)
  { cx: 242, cy: 152, rot:  -3, rx: 1.4, ry: 3.0 }, { cx: 256, cy: 156, rot:  37, rx: 1.4, ry: 3.0 },
  // pair 5L ( 31,154,−17°) → perp=(0.96,−0.29)
  { cx:  24, cy: 156, rot: -37, rx: 1.4, ry: 3.0 }, { cx:  38, cy: 152, rot:   3, rx: 1.4, ry: 3.0 },
  // pair 6  (240,67, −5°) → perp=(1.00,−0.09)
  { cx: 233, cy:  68, rot: -25, rx: 1.4, ry: 3.0 }, { cx: 247, cy:  66, rot:  15, rx: 1.4, ry: 3.0 },
  // pair 6L ( 40,67,  5°) → perp=(1.00, 0.09)
  { cx:  33, cy:  66, rot: -15, rx: 1.4, ry: 3.0 }, { cx:  47, cy:  68, rot:  25, rx: 1.4, ry: 3.0 },
  // pair 7  (238,151,−34°) → perp=(0.83,−0.56)
  { cx: 232, cy: 155, rot: -54, rx: 1.4, ry: 3.0 }, { cx: 244, cy: 147, rot: -14, rx: 1.4, ry: 3.0 },
  // pair 7L ( 42,151, 34°) → perp=(0.83, 0.56)
  { cx:  36, cy: 147, rot:  14, rx: 1.4, ry: 3.0 }, { cx:  48, cy: 155, rot:  54, rx: 1.4, ry: 3.0 },
  // pair 8  (246,200,−13°) → perp=(0.97,−0.22)
  { cx: 239, cy: 202, rot: -33, rx: 1.4, ry: 3.0 }, { cx: 253, cy: 198, rot:   7, rx: 1.4, ry: 3.0 },
  // pair 8L ( 34,200, 13°) → perp=(0.97, 0.22)
  { cx:  27, cy: 198, rot:  -7, rx: 1.4, ry: 3.0 }, { cx:  41, cy: 202, rot:  33, rx: 1.4, ry: 3.0 },
  // fork 0R (180,151, 15°) → perp=(0.97, 0.26)
  { cx: 173, cy: 149, rot:  -5, rx: 1.4, ry: 3.0 }, { cx: 187, cy: 153, rot:  35, rx: 1.4, ry: 3.0 },
  // fork 0L (100,151,−15°) → perp=(0.97,−0.26)
  { cx:  93, cy: 153, rot: -35, rx: 1.4, ry: 3.0 }, { cx: 107, cy: 149, rot:   5, rx: 1.4, ry: 3.0 },
  // fork 1R (210,180, −5°) → perp=(1.00,−0.09)
  { cx: 203, cy: 181, rot: -25, rx: 1.4, ry: 3.0 }, { cx: 217, cy: 179, rot:  15, rx: 1.4, ry: 3.0 },
  // fork 1L ( 70,180,  5°) → perp=(1.00, 0.09)
  { cx:  63, cy: 179, rot: -15, rx: 1.4, ry: 3.0 }, { cx:  77, cy: 181, rot:  25, rx: 1.4, ry: 3.0 },
  // fork 2R (165,195, 50°) → perp=(0.64, 0.77)
  { cx: 161, cy: 190, rot:  30, rx: 1.4, ry: 3.0 }, { cx: 170, cy: 200, rot:  70, rx: 1.4, ry: 3.0 },
  // fork 2L (115,195,−50°) → perp=(0.64,−0.77)
  { cx: 120, cy: 190, rot: -30, rx: 1.4, ry: 3.0 }, { cx: 111, cy: 200, rot: -70, rx: 1.4, ry: 3.0 },
];

// ── 4-point color cycle (each phase interpolates to its target over durationMs)
// grow  → sky blue   (expansion, life)
// hold+ → teal       (calm fullness, subtle effort)
// shrink → warm straw (release, earth)
// hold- → sage green  (difficulty passing, life returning)
const PHASE_END_COLORS: Partial<Record<CircleAction, string>> = {
  [CircleAction.grow]:      '#8BBCD6',
  [CircleAction.holdLarge]: '#6AA8BA',
  [CircleAction.shrink]:    '#C4AA78',
  [CircleAction.holdSmall]: '#8DC4A0',
};
const IDLE_COLOR = '#8DC4A0';

function lerpHex(a: string, b: string, t: number): string {
  const p = (h: string, i: number) => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
  const vals = [0, 1, 2].map(i => Math.round(p(a, i) + (p(b, i) - p(a, i)) * t));
  return '#' + vals.map(v => v.toString(16).padStart(2, '0')).join('');
}

const BRANCHES_BY_LEVEL: string[][] = [
  ['M 140,298 L 140,222'],
  [
    'M 140,222 C 145,212 160,204 170,191',
    'M 140,222 C 135,212 120,206 110,194',
  ],
  [
    'M 170,191 C 174,178 180,163 184,147',
    'M 170,191 C 178,187 195,183 205,176',
    'M 110,194 C 106,181 100,165 96,148',
    'M 110,194 C 102,190 85,185 75,178',
  ],
  [
    'M 184,147 C 186,133 186,118 185,103',
    'M 184,147 C 191,136 201,126 205,112',
    'M 205,176 C 213,165 218,153 220,139',
    'M 205,176 C 213,176 224,178 230,179',
    'M 96,148 C 94,133 94,118 95,103',
    'M 96,148 C 89,136 79,126 75,112',
    'M 75,178 C 67,166 62,153 60,139',
    'M 75,178 C 67,178 56,179 50,180',
  ],
  [
    'M 185,103 C 183,89 180,78 177,66',
    'M 185,103 C 191,92 198,81 203,70',
    'M 205,112 C 212,99 217,87 219,73',
    'M 205,112 C 213,112 221,112 227,110',
    'M 220,139 C 227,127 233,115 235,102',
    'M 230,179 C 239,173 246,164 249,154',
    'M 95,103 C 97,89 100,78 103,66',
    'M 95,103 C 89,92 82,81 77,70',
    'M 75,112 C 68,99 63,87 61,73',
    'M 75,112 C 67,112 59,112 53,110',
    'M 60,139 C 53,127 47,115 45,102',
    'M 50,180 C 41,173 34,164 31,154',
    // 6 extra branches: second children for the 4 single-child level-3 nodes,
    // plus one extension each from the outer mid-height tips (symmetric)
    'M 220,139 C 227,141 234,145 238,151',
    'M 230,179 C 238,184 244,191 246,200',
    'M 235,102 C 239,91 241,79 240,67',
    'M 60,139 C 53,141 46,145 42,151',
    'M 50,180 C 42,184 36,191 34,200',
    'M 45,102 C 41,91 39,79 40,67',
  ],
];

// easeOutBack — springy bloom feel
function easeOutBack(t: number): number {
  return 1 + 2.5 * Math.pow(t - 1, 3) + 1.5 * Math.pow(t - 1, 2);
}

const STROKE_WIDTHS = [3.0, 2.3, 1.7, 1.05, 0.55];
const MAX_LEVEL = BRANCHES_BY_LEVEL.length - 1;
const MIN_STAGGER_MS = 50;

const ARC_CX = 140;
const ARC_CY = 160;
const ARC_RX = 148;
const ARC_RY = 163;
const _h = ((ARC_RX - ARC_RY) ** 2) / ((ARC_RX + ARC_RY) ** 2);
const ARC_CIRCUMFERENCE =
  Math.PI * (ARC_RX + ARC_RY) * (1 + (3 * _h) / (10 + Math.sqrt(4 - 3 * _h)));

export interface LungAnimationProps {
  /** Accepted for API parity with BreathSphere — color is driven by phase/duration, not size. */
  circleSize: number;
  durationMs: number;
  phase: CircleAction;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function LungAnimation({ circleSize: _circleSize, durationMs, phase }: LungAnimationProps) {
  const id = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const arcRef = useRef<SVGEllipseElement>(null);
  const dotRef = useRef<SVGEllipseElement>(null);
  const leavesRef = useRef<SVGGElement>(null);
  // Branch timers — cleared on every phase change
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Leaf timers — only cleared when a new leaf animation (reveal/hide) starts,
  // so pending reveals survive the grow→holdLarge phase transition
  const leafTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const leafRafs = useRef<number[]>([]);

  const clearLeafTimers = useCallback(() => {
    leafTimers.current.forEach(clearTimeout);
    leafTimers.current = [];
  }, []);

  const clearLeafRafs = useCallback(() => {
    leafRafs.current.forEach(cancelAnimationFrame);
    leafRafs.current = [];
  }, []);

  // ── Per-phase color state — snapshotted on each phase change
  const colorStateRef = useRef({ from: IDLE_COLOR, to: IDLE_COLOR, startMs: 0, totalMs: 1000 });

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const getLevelGroup = useCallback(
    (level: number): SVGGElement | null =>
      svgRef.current?.querySelector<SVGGElement>(`[data-level="${level}"]`) ?? null,
    [],
  );

  // ── Color animation — rAF loop interpolates from→to over durationMs
  useEffect(() => {
    const toColor = PHASE_END_COLORS[phase] ?? IDLE_COLOR;
    const prev = colorStateRef.current;
    const elapsed = performance.now() - prev.startMs;
    const prevT = prev.totalMs > 0 ? Math.min(elapsed / prev.totalMs, 1) : 1;
    const fromColor = lerpHex(prev.from, prev.to, prevT);

    colorStateRef.current = { from: fromColor, to: toColor, startMs: performance.now(), totalMs: durationMs };

    const svg = svgRef.current;
    let rafId: number;

    const tick = () => {
      const s = colorStateRef.current;
      const t = s.totalMs > 0 ? Math.min((performance.now() - s.startMs) / s.totalMs, 1) : 1;
      const color = lerpHex(s.from, s.to, t);
      if (svg) svg.style.setProperty('--lung-anim-color', color);
      if (t < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [phase, durationMs]);

  const revealLeaves = useCallback((delayMs: number) => {
    // Cancel any previous leaf animation before starting a new one
    clearLeafTimers();
    clearLeafRafs();
    const grp = leavesRef.current;
    if (!grp) return;
    grp.querySelectorAll<SVGEllipseElement>('ellipse').forEach((leaf, i) => {
      const { rot } = LEAVES[i];
      // Use leafTimers (not timers) so these survive the grow→holdLarge phase transition
      const t = setTimeout(() => {
        const start = performance.now();
        const dur = 480;
        const tick = (now: number) => {
          const p = Math.min((now - start) / dur, 1);
          const s = Math.max(0, easeOutBack(p));
          leaf.setAttribute('transform', `rotate(${rot}) scale(${s})`);
          leaf.style.opacity = String(Math.min(p * 2.5, 0.82));
          if (p < 1) leafRafs.current.push(requestAnimationFrame(tick));
        };
        leafRafs.current.push(requestAnimationFrame(tick));
      }, delayMs + i * 12);
      leafTimers.current.push(t);
    });
  }, [clearLeafTimers, clearLeafRafs]);

  const hideLeaves = useCallback((delayMs: number) => {
    // Cancel any pending reveal timers and in-progress bloom rAFs
    clearLeafTimers();
    clearLeafRafs();
    const grp = leavesRef.current;
    if (!grp) return;
    grp.querySelectorAll<SVGEllipseElement>('ellipse').forEach((leaf, i) => {
      const { rot } = LEAVES[i];
      const t = setTimeout(() => {
        const start = performance.now();
        const dur = 300;
        const tick = (now: number) => {
          const p = Math.min((now - start) / dur, 1);
          const s = 1 - p * p;
          leaf.setAttribute('transform', `rotate(${rot}) scale(${s})`);
          leaf.style.opacity = String(0.82 * (1 - p));
          if (p < 1) leafRafs.current.push(requestAnimationFrame(tick));
        };
        leafRafs.current.push(requestAnimationFrame(tick));
      }, delayMs);
      leafTimers.current.push(t);
    });
  }, [clearLeafTimers, clearLeafRafs]);

  const reveal = useCallback((totalMs: number) => {
    clearTimers();
    const stagger = Math.max(MIN_STAGGER_MS, (totalMs - 600) / MAX_LEVEL);
    for (let lvl = 0; lvl <= MAX_LEVEL; lvl++) {
      const t = setTimeout(() => {
        const grp = getLevelGroup(lvl);
        if (!grp) return;
        grp.style.transition = 'opacity 0.6s ease';
        grp.style.opacity = '1';
      }, lvl * stagger);
      timers.current.push(t);
    }
    // Skip leaf bloom for rapid techniques — the 480ms bloom outlasts the step
    if (totalMs >= 1000) {
      revealLeaves(MAX_LEVEL * stagger + 300);
    }
  }, [clearTimers, getLevelGroup, revealLeaves]);

  const conceal = useCallback((totalMs: number) => {
    clearTimers();
    if (totalMs >= 1000) hideLeaves(0);
    const stagger = Math.max(MIN_STAGGER_MS, (totalMs - 700) / MAX_LEVEL);
    for (let lvl = 0; lvl <= MAX_LEVEL; lvl++) {
      const reverseLevel = MAX_LEVEL - lvl;
      const t = setTimeout(() => {
        const grp = getLevelGroup(lvl);
        if (!grp) return;
        grp.style.transition = 'opacity 0.7s ease';
        grp.style.opacity = '0';
      }, reverseLevel * stagger);
      timers.current.push(t);
    }
  }, [clearTimers, getLevelGroup, hideLeaves]);

  useArcAnimation(arcRef, dotRef, ARC_CIRCUMFERENCE, phase, durationMs);

  useEffect(() => {
    switch (phase) {
      case CircleAction.grow:
        reveal(durationMs);
        break;
      case CircleAction.shrink:
        conceal(durationMs);
        break;
      case CircleAction.holdLarge:
      case CircleAction.holdSmall:
      default:
        break;
    }
    // Only cancel branch timers on phase change — leaf timers live in leafTimers
    // and are managed exclusively by revealLeaves/hideLeaves
    return clearTimers;
  }, [phase, durationMs, reveal, conceal, clearTimers]);

  return (
    <div className={styles.wrapper}>
      <svg
        ref={svgRef}
        className={styles.svg}
        viewBox="0 0 280 320"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ '--lung-anim-color': IDLE_COLOR } as React.CSSProperties}
      >
        <defs>
          <filter id={`lung-glow-${id}`} x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="5" result="blur1" />
            <feGaussianBlur stdDeviation="2" result="blur2" />
            <feMerge>
              <feMergeNode in="blur1" />
              <feMergeNode in="blur2" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={`lung-glow-dormant-${id}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="0.8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <ellipse
          ref={arcRef}
          cx={ARC_CX}
          cy={ARC_CY}
          rx={ARC_RX}
          ry={ARC_RY}
          fill="none"
          strokeWidth="1.2"
          strokeLinecap="round"
          transform={`rotate(-90 ${ARC_CX} ${ARC_CY})`}
          style={{
            stroke: 'var(--lung-anim-color)',
            strokeDasharray: `${ARC_CIRCUMFERENCE} ${ARC_CIRCUMFERENCE}`,
            strokeDashoffset: ARC_CIRCUMFERENCE,
          }}
        />

        {/* Progress dot — orbits the arc ellipse once per phase */}
        <ellipse
          ref={dotRef}
          cx={ARC_CX}
          cy={ARC_CY}
          rx={ARC_RX}
          ry={ARC_RY}
          fill="none"
          strokeWidth="3.5"
          strokeLinecap="round"
          transform={`rotate(-90 ${ARC_CX} ${ARC_CY})`}
          style={{
            stroke: 'var(--lung-anim-color)',
            strokeDasharray: `4 ${ARC_CIRCUMFERENCE}`,
            strokeDashoffset: 0,
          }}
        />

        <g filter={`url(#lung-glow-dormant-${id})`} opacity="0.09">
          {BRANCHES_BY_LEVEL.map((levelPaths, level) =>
            levelPaths.map((d, i) => (
              <path
                key={`dormant-${level}-${i}`}
                d={d}
                fill="none"
                stroke="rgba(125,212,142,1)"
                strokeWidth={STROKE_WIDTHS[level]}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )),
          )}
        </g>

        <g filter={`url(#lung-glow-${id})`}>
          {BRANCHES_BY_LEVEL.map((levelPaths, level) => (
            <g
              key={level}
              data-level={level}
              style={{ opacity: 0, transition: 'opacity 0.6s ease' }}
            >
              {levelPaths.map((d, i) => (
                <path
                  key={i}
                  d={d}
                  fill="none"
                  style={{ stroke: 'var(--lung-anim-color)' }}
                  strokeWidth={STROKE_WIDTHS[level]}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
            </g>
          ))}
        </g>

        {/* Leaves — bloom at branch tips; SVG transform attr used (not CSS) so
            scale always collapses to the translate-group origin = leaf center */}
        <g ref={leavesRef} filter={`url(#lung-glow-${id})`}>
          {LEAVES.map((leaf, i) => (
            <g key={i} transform={`translate(${leaf.cx}, ${leaf.cy})`}>
              <ellipse
                cx={0}
                cy={0}
                rx={leaf.rx ?? 2.4}
                ry={leaf.ry ?? 5.2}
                stroke="none"
                transform={`rotate(${leaf.rot}) scale(0)`}
                style={{ opacity: 0, fill: '#F2A7BB' }}
              />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
